import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { createPatient } from "../api/patients";
import PatientIdBadge from "../components/PatientIdBadge";
import DiseasesSelector from "../components/DiseasesSelector";

const EDUCATION_OPTIONS = [
  { value: "", label: "Не указано" },
  { value: "incompleteSecondaryEducation", label: "Неполное среднее" },
  { value: "secondaryEducation", label: "Среднее" },
  { value: "incompleteHigherEducation", label: "Неполное высшее" },
  { value: "higherEducation", label: "Высшее" },
  { value: "academicDegree", label: "Учёная степень" },
];

const LANGUAGE_OPTIONS = [
  { value: "KK", label: "Қазақша" },
  { value: "RU", label: "Русский" },
  { value: "EN", label: "English" },
];

interface CreatePatientPageProps {
  homePath?: string;
  patientBasePath?: string;
}

export default function CreatePatientPage({
  homePath = "/doctor",
  patientBasePath = "/patient",
}: CreatePatientPageProps = {}) {
  const navigate = useNavigate();
  const [sex, setSex] = useState("");
  const [age, setAge] = useState("");
  const [education, setEducation] = useState("");
  const [language, setLanguage] = useState("KK");
  const [notes, setNotes] = useState("");
  const [diseases, setDiseases] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [createdId, setCreatedId] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const patient = await createPatient({
        sex,
        age: parseInt(age),
        education: education || undefined,
        language: language || undefined,
        free_notes: notes || undefined,
        diseases: diseases.length > 0 ? diseases : undefined,
      });
      setCreatedId(patient.patient_id);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Ошибка при создании пациента");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Новый пациент
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <FormControl fullWidth margin="normal" required>
          <InputLabel>Пол</InputLabel>
          <Select value={sex} label="Пол" onChange={(e) => setSex(e.target.value)}>
            <MenuItem value="male">Мужской</MenuItem>
            <MenuItem value="female">Женский</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Возраст"
          type="number"
          value={age}
          onChange={(e) => setAge(e.target.value)}
          margin="normal"
          required
          slotProps={{ htmlInput: { min: 1, max: 120 } }}
        />

        <FormControl fullWidth margin="normal">
          <InputLabel>Образование</InputLabel>
          <Select
            value={education}
            label="Образование"
            onChange={(e) => setEducation(e.target.value)}
          >
            {EDUCATION_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel>Язык</InputLabel>
          <Select
            value={language}
            label="Язык"
            onChange={(e) => setLanguage(e.target.value)}
          >
            {LANGUAGE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Заметки"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          margin="normal"
          multiline
          rows={3}
        />

        <Box sx={{ mt: 2 }}>
          <DiseasesSelector value={diseases} onChange={setDiseases} />
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading}
          sx={{ mt: 2 }}
        >
          {loading ? "Создание..." : "Создать пациента"}
        </Button>
      </form>

      <Dialog open={!!createdId} onClose={() => navigate(homePath)}>
        <DialogTitle>Пациент создан</DialogTitle>
        <DialogContent sx={{ textAlign: "center", py: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            ID пациента:
          </Typography>
          {createdId && <PatientIdBadge patientId={createdId} />}
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 2 }}>
            Запишите этот ID для дальнейшего использования
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate(`${patientBasePath}/${createdId}`)}>
            Открыть карточку
          </Button>
          <Button onClick={() => navigate(homePath)} variant="contained">
            На главную
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
